/**
 * PopularDestinations - Fetches and displays top indexable destinations.
 * Used in Homepage and Footer for SEO internal linking.
 *
 * 2026-04 update:
 *   - Merges price snapshot from /api/destinations/price-index so cards can
 *     show "vanaf €X — €Y/nacht" (Booking.com-style) even BEFORE the user
 *     selects dates.
 *   - Emits Schema.org JSON-LD (TouristDestination + ItemList) so Google rich
 *     snippets + AI crawlers (GPTBot/ClaudeBot/PerplexityBot) can cite our
 *     prices directly in their answers.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

/** Resolve image URL: prefix backend host for relative /static/... paths */
const resolveImg = (u) => {
  if (!u) return null;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) return `${BASE}${u}`;
  return u;
};

// Shared caches
let _cachedDestinations = null;
let _cachedPriceIndex = null;
let _priceIndexLoaded = false;

export function usePopularDestinations(limit = 20) {
  const [destinations, setDestinations] = useState(_cachedDestinations || []);
  const [priceIndex, setPriceIndex] = useState(_cachedPriceIndex || []);
  const [loading, setLoading] = useState(!_cachedDestinations);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [destRes, priceRes] = await Promise.all([
          _cachedDestinations
            ? Promise.resolve({ data: { destinations: _cachedDestinations } })
            : axios.get(`${API}/seo/destinations?limit=${limit}`),
          _priceIndexLoaded
            ? Promise.resolve({ data: { destinations: _cachedPriceIndex } })
            : axios.get(`${API}/destinations/price-index?limit=80`).catch(() => ({ data: { destinations: [] } })),
        ]);
        if (cancelled) return;
        _cachedDestinations = destRes.data?.destinations || [];
        _cachedPriceIndex = priceRes.data?.destinations || [];
        _priceIndexLoaded = true;
        setDestinations(_cachedDestinations);
        setPriceIndex(_cachedPriceIndex);
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [limit]);

  // Merge price info by matching city (case-insensitive)
  const merged = useMemo(() => {
    if (!destinations?.length) return [];
    const priceMap = new Map();
    for (const p of priceIndex || []) {
      const k = (p.city || '').toLowerCase().trim();
      if (k) priceMap.set(k, p);
    }
    return destinations.map(d => {
      const k = (d.city || '').toLowerCase().trim();
      const p = priceMap.get(k);
      return p
        ? {
            ...d,
            price_from_eur: p.price_from_eur,
            price_to_eur: p.price_to_eur,
            price_avg_eur: p.price_avg_eur,
            price_currency: p.currency || 'EUR',
            price_sampled_checkin: p.sampled_checkin,
          }
        : d;
    });
  }, [destinations, priceIndex]);

  return { destinations: merged, loading };
}

/** Schema.org JSON-LD for destination list — boosts rich snippets + AI citations */
function DestinationsJsonLd({ destinations, baseUrl }) {
  if (!destinations?.length) return null;
  const itemList = destinations.slice(0, 20).map((d, i) => {
    const url = `${baseUrl}/${d.country_slug}/${d.city_slug}`;
    const base = {
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'TouristDestination',
        name: d.city,
        url,
        ...(d.country ? { address: { '@type': 'PostalAddress', addressCountry: d.country } } : {}),
      },
    };
    if (d.price_from_eur && d.price_to_eur) {
      base.item.includesAttraction = {
        '@type': 'AggregateOffer',
        priceCurrency: d.price_currency || 'EUR',
        lowPrice: d.price_from_eur,
        highPrice: d.price_to_eur,
        offerCount: d.hotel_count || 20,
        description: '3 nights, 2 adults, 14 days ahead — indicative per night',
      };
    }
    return base;
  });
  const payload = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: itemList,
  };
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(payload) }}
    />
  );
}

function PriceBadge({ dest }) {
  if (!dest.price_from_eur) return null;
  const from = Math.round(dest.price_from_eur);
  const to = dest.price_to_eur ? Math.round(dest.price_to_eur) : null;
  return (
    <span
      className="hidden sm:inline-block shrink-0 text-[10px] font-semibold text-[#E8A317] bg-[#E8A317]/10 border border-[#E8A317]/30 rounded-full px-1.5 py-0.5 ml-1"
      title="Indicative price per night, 2 adults, 3 nights, 14 days ahead"
      data-testid="dest-price-badge"
    >
      {to && to !== from ? `€${from}–${to}` : `from €${from}`}
    </span>
  );
}

/** Grid of destination links for Homepage */
export function PopularDestinationsGrid({ limit = 8 }) {
  const { destinations, loading } = usePopularDestinations(limit);
  const { t } = useTranslation();
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://www.freestays.eu';

  if (loading || destinations.length === 0) return null;

  return (
    <section className="py-12 md:py-16" data-testid="popular-destinations-section">
      <DestinationsJsonLd destinations={destinations} baseUrl={baseUrl} />
      <div className="max-w-[1760px] mx-auto px-4 md:px-8">
        <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-2 text-center" style={{ textShadow: '2px 3px 6px rgba(0,0,0,0.5)' }}>
          <span className="block text-white">{t('popular.smartTitle1', 'Trending Destinations')}</span>
          <span className="block" style={{ color: '#E8A317' }}>{t('popular.smartTitle2', 'Explore the World')}</span>
        </h2>
        <p className="text-sm text-muted-foreground mb-6 text-center">{t('popular.subtitle', 'Explore our most popular hotel destinations')}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-8 gap-2.5">
          {destinations.slice(0, limit).map((dest) => {
            const imgSrc = resolveImg(dest.image_url);
            return (
              <Link
                key={`${dest.country_slug}-${dest.city_slug}`}
                to={`/${dest.country_slug}/${dest.city_slug}`}
                className="group flex items-center gap-2 bg-white dark:bg-[#0a2540] border border-gray-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-sm hover:border-[#00A9E0] hover:bg-[#00A9E0]/5 transition-colors"
                data-testid={`dest-link-${dest.city_slug}`}
              >
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={dest.city}
                    loading="lazy"
                    className="w-8 h-8 rounded-md object-cover shrink-0 border border-white/10"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#00A9E0]/40 to-[#081D33]/60 flex items-center justify-center shrink-0">
                    <MapPin className="w-3.5 h-3.5 text-white/70" />
                  </div>
                )}
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="truncate text-foreground text-[13px] leading-tight font-medium">{dest.city}</span>
                  <div className="flex items-center gap-1.5 text-[10px] leading-tight">
                    <PriceBadge dest={dest} />
                    <span className="text-muted-foreground">
                      {dest.hotel_count >= 1000 ? `${Math.round(dest.hotel_count / 1000)}k` : dest.hotel_count}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        {destinations.some(d => d.price_from_eur) && (
          <p className="text-[11px] text-muted-foreground/80 mt-3 text-center">
            {t('popular.priceFootnote', '* Indicatieve prijs per nacht — 2 volwassenen, 3 nachten, 14 dagen vooruit. Werkelijke prijzen afhankelijk van beschikbaarheid.')}
          </p>
        )}
      </div>
    </section>
  );
}

/** Carousel variant of PopularDestinations — used on Homepage in addition to the grid+footer.
 * Same data source as `usePopularDestinations`, but renders top N as horizontally scrollable cards. */
export function PopularDestinationsCarousel({ limit = 8 }) {
  const { destinations, loading } = usePopularDestinations(Math.max(limit, 16));
  const { t } = useTranslation();
  const scrollerRef = React.useRef(null);

  if (loading || destinations.length === 0) return null;

  const items = destinations.slice(0, limit);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section className="py-12 bg-secondary/20" data-testid="popular-destinations-carousel">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              {t('popular.smartTitle1', 'Trending Destinations')}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('popular.subtitle', 'Explore our most popular hotel destinations')}
            </p>
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scroll(-1)}
              aria-label="Scroll left"
              className="h-9 w-9 rounded-full border border-border bg-card hover:bg-accent flex items-center justify-center transition"
              data-testid="popular-scroll-prev"
            >
              ‹
            </button>
            <button
              onClick={() => scroll(1)}
              aria-label="Scroll right"
              className="h-9 w-9 rounded-full border border-border bg-card hover:bg-accent flex items-center justify-center transition"
              data-testid="popular-scroll-next"
            >
              ›
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-thin"
          style={{ scrollPaddingLeft: '1rem' }}
        >
          {items.map((dest) => (
            <Link
              key={`${dest.country_slug}-${dest.city_slug}`}
              to={`/${dest.country_slug}/${dest.city_slug}`}
              className="group relative min-w-[220px] md:min-w-[260px] max-w-[260px] rounded-2xl bg-card border border-border overflow-hidden snap-start hover:shadow-xl hover:-translate-y-0.5 transition-all"
              data-testid={`pop-dest-card-${dest.city_slug}`}
            >
              <div className="relative aspect-[4/3] bg-muted overflow-hidden">
                {dest.image_url ? (
                  <img
                    src={resolveImg(dest.image_url)}
                    alt={dest.city}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#00A9E0]/30 to-[#081D33]/40 flex items-center justify-center">
                    <MapPin className="w-10 h-10 text-white/80" />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
                  <h3 className="font-semibold text-base line-clamp-1">{dest.city}</h3>
                  {dest.country && (
                    <p className="text-xs opacity-80 line-clamp-1">{dest.country}</p>
                  )}
                </div>
              </div>
              <div className="p-3 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {dest.hotel_count >= 1000 ? `${Math.round(dest.hotel_count / 1000)}k` : dest.hotel_count} {t('popular.hotels', 'hotels')}
                </span>
                {dest.price_from_eur && (
                  <span className="font-semibold text-[#E8A317]">
                    {t('popular.from', 'from')} €{Math.round(dest.price_from_eur)}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


/** Compact list of destination links for Footer - full width row */
export function FooterDestinationLinks({ limit = 12 }) {
  const { destinations } = usePopularDestinations(limit);

  if (destinations.length === 0) return null;

  return (
    <div data-testid="footer-destinations">
      <h4 className="font-semibold mb-4">Popular Destinations</h4>
      <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-2 text-sm text-muted-foreground">
        {destinations.slice(0, limit).map((dest) => (
          <li key={`${dest.country_slug}-${dest.city_slug}`}>
            <Link
              to={`/${dest.country_slug}/${dest.city_slug}`}
              className="hover:text-[#00A9E0] transition-colors inline-flex items-center gap-1.5"
              data-testid={`footer-dest-${dest.city_slug}`}
            >
              <span>{dest.city}</span>
              {dest.price_from_eur && (
                <span className="text-[10px] text-[#E8A317]/80 font-medium">
                  €{Math.round(dest.price_from_eur)}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
